/**
 * =============================================================================
 * QUESTION REJECT MODAL (ADR-025 A-Q1 — BL-ADMIN-WORKBENCH-IA-001B / OD-19)
 * =============================================================================
 *
 * Required-reason confirmation for whole-question rejection. Until this dialog,
 * question Reject fired on ONE unguarded click with no reason — the only
 * moderation surface without the audited-justification parity that reply and
 * image rejection already have (ADR-017 §3.8).
 *
 * The reason is a CONTROLLED VOCABULARY Select (`QUESTION_REJECTION_REASONS`,
 * OD-19 — modeled on `MediaRejectModal`, NOT the free-text reply modal), plus
 * an OPTIONAL note. The note is stored in the audit row's metadata but is
 * EXCLUDED from the ADR-023 display-safe projection — only the code and a
 * "note recorded" marker ever render.
 *
 * Built on the A-0 `AccessibleModal` primitive → M11 hard-pass by construction
 * (guaranteed dialog name, Esc, focus trap, focus restore).
 * =============================================================================
 */

import React, { useEffect, useState } from 'react';
import { Box, Select, Text, Textarea } from '@bigcommerce/big-design';
import {
  QUESTION_REJECTION_REASONS,
  QUESTION_REJECTION_NOTE_MAX_LENGTH,
  type QuestionRejectionReason,
} from '@find-my-part/types';
import { AccessibleModal } from '../common/AccessibleModal';
import {
  canReapplyQuestionStatusIssue,
  QuestionStatusWriteNotice,
  type QuestionStatusWriteIssue,
} from './QuestionStatusWriteNotice';

/** UI labels for the controlled vocabulary (codes are the API contract). */
export const QUESTION_REJECTION_REASON_LABELS: Record<QuestionRejectionReason, string> = {
  NOT_RELEVANT: 'Not relevant / off-topic',
  SPAM: 'Spam or advertising',
  DUPLICATE: 'Duplicate question',
  INAPPROPRIATE: 'Inappropriate content',
  PERSONAL_INFO: 'Contains personal information',
  UNANSWERABLE: 'Cannot be answered as asked',
  OTHER: 'Other',
};

export interface QuestionRejectModalProps {
  /** Whether the dialog is open. */
  isOpen: boolean;
  /** Title of the question being rejected (context for the moderator). */
  questionTitle?: string;
  /** True while the rejection request is in flight (disables inputs/actions). */
  isSubmitting?: boolean;
  /** Confirm: reject with the selected reason (+ optional trimmed note). */
  onConfirm: (reason: QuestionRejectionReason, note?: string) => void;
  /** Cancel/close without rejecting. */
  onCancel: () => void;
  /** ADR-027 status-write outcome; rendered inside the still-open draft dialog. */
  statusWriteIssue?: QuestionStatusWriteIssue | null;
  /** Reapply using the dialog's CURRENT reason/note, not a captured stale draft. */
  onStatusWriteReapply?: (reason: QuestionRejectionReason, note?: string) => void;
  /** Confirm an ambiguous outcome, or reload after a stale-tab protocol rejection. */
  onStatusWriteRefresh?: () => void;
  /** Dismiss/accept the status-write outcome while preserving modal context. */
  onStatusWriteDismiss?: () => void;
  /**
   * Forwarded to A-0: focus target when the trigger is unmounted at close
   * (confirmed rejection removes/transforms the row, or closes the detail
   * modal). Cancel paths still restore to the live trigger.
   */
  fallbackFocusTo?: React.RefObject<HTMLElement> | (() => HTMLElement | null);
  /** Optional test id prefix. */
  testId?: string;
}

export function QuestionRejectModal({
  isOpen,
  questionTitle,
  isSubmitting = false,
  onConfirm,
  onCancel,
  statusWriteIssue = null,
  onStatusWriteReapply,
  onStatusWriteRefresh,
  onStatusWriteDismiss,
  fallbackFocusTo,
  testId,
}: QuestionRejectModalProps): React.ReactElement {
  const [reason, setReason] = useState<QuestionRejectionReason | undefined>(undefined);
  const [note, setNote] = useState('');

  // Fresh form every time the dialog opens (a stale reason from a previous
  // rejection must never pre-arm the destructive action).
  useEffect(() => {
    if (isOpen) {
      setReason(undefined);
      setNote('');
    }
  }, [isOpen]);

  const trimmedNote = note.trim();
  const noteTooLong = trimmedNote.length > QUESTION_REJECTION_NOTE_MAX_LENGTH;
  const canConfirm =
    reason !== undefined && !noteTooLong && !isSubmitting && statusWriteIssue === null;

  const handleConfirm = (): void => {
    if (!canConfirm || reason === undefined) return;
    onConfirm(reason, trimmedNote.length > 0 ? trimmedNote : undefined);
  };

  return (
    <AccessibleModal
      isOpen={isOpen}
      fallbackFocusTo={fallbackFocusTo}
      header="Reject question"
      onClose={isSubmitting ? () => undefined : onCancel}
      closeOnEscKey={!isSubmitting}
      closeOnClickOutside={false}
      actions={[
        {
          text: 'Cancel',
          variant: 'subtle',
          onClick: onCancel,
          disabled: isSubmitting,
        },
        {
          text: 'Reject question',
          actionType: 'destructive' as const,
          onClick: handleConfirm,
          disabled: !canConfirm,
          isLoading: isSubmitting,
        },
      ]}
    >
      <Box data-testid={testId ? `${testId}-reject-modal` : undefined}>
        <QuestionStatusWriteNotice
          issue={statusWriteIssue}
          busy={isSubmitting}
          onReapply={
            canReapplyQuestionStatusIssue(statusWriteIssue) &&
            reason !== undefined &&
            !noteTooLong &&
            onStatusWriteReapply
              ? () => onStatusWriteReapply(reason, trimmedNote.length > 0 ? trimmedNote : undefined)
              : undefined
          }
          onRefresh={onStatusWriteRefresh}
          onDismiss={onStatusWriteDismiss}
          restoreFocusTo={() => {
            const dialog = document.querySelector<HTMLElement>('[role="dialog"]');
            if (!dialog) return null;
            const buttons = Array.from(dialog.querySelectorAll<HTMLButtonElement>('button'));
            return (
              buttons.find((button) => button.textContent?.trim() === 'Reject question') ??
              buttons.find((button) => button.textContent?.trim() === 'Cancel') ??
              null
            );
          }}
          testId={testId ? `${testId}-reject-status-write` : 'reject-status-write'}
        />
        {questionTitle ? (
          <Text marginBottom="small">
            Rejecting: <strong>{questionTitle}</strong>
          </Text>
        ) : null}
        <Text color="secondary" marginBottom="medium">
          A reason is required. It is recorded in the audit trail; the optional note is stored for
          internal context and is never displayed.
        </Text>
        <Select
          label="Reason"
          required
          placeholder="Select a reason"
          options={QUESTION_REJECTION_REASONS.map((code) => ({
            value: code,
            content: QUESTION_REJECTION_REASON_LABELS[code],
          }))}
          value={reason}
          onOptionChange={(value) => setReason(value as QuestionRejectionReason | undefined)}
          disabled={isSubmitting}
          data-testid={testId ? `${testId}-reject-reason` : undefined}
        />
        <Box marginTop="medium">
          <Textarea
            label={`Internal note (optional, ${QUESTION_REJECTION_NOTE_MAX_LENGTH} characters max)`}
            description={
              noteTooLong
                ? `${trimmedNote.length}/${QUESTION_REJECTION_NOTE_MAX_LENGTH} — too long`
                : `${trimmedNote.length}/${QUESTION_REJECTION_NOTE_MAX_LENGTH}`
            }
            error={
              noteTooLong
                ? `Must be ${QUESTION_REJECTION_NOTE_MAX_LENGTH} characters or fewer`
                : undefined
            }
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={isSubmitting}
            rows={3}
            data-testid={testId ? `${testId}-reject-note` : undefined}
          />
        </Box>
      </Box>
    </AccessibleModal>
  );
}
